/**
 * Web Push subscription helpers (Phase 11 / M9).
 *
 * Lifecycle:
 *   1. `getSubscriptionStatus()` — synchronous-ish probe of the current
 *      browser state. Used to render the toggle's correct position on
 *      mount.
 *   2. `subscribeToPush()` — asks the user for permission (if needed),
 *      calls `pushManager.subscribe` with the VAPID public key from env,
 *      and POSTs the result to the gateway so the backend can target
 *      this device when an approval is required.
 *   3. `unsubscribeFromPush()` — flips the subscription off and tells
 *      the backend to drop the record (best-effort; revoke on the server
 *      side too if the call fails).
 *   4. `sendTestPush()` — triggers a self-test via the gateway so the
 *      founder can confirm permission grant + SW handler + payload route
 *      without waiting for a real approval gate to fire.
 *
 * All errors surface as typed `PushSubscribeError` so the settings page
 * can render a useful message instead of a console-only stack trace.
 *
 * The applicationServerKey is the URL-safe base64 VAPID public key. The
 * browser API insists on a `Uint8Array`, so we decode here.
 */
import { env } from '../env';

import { apiFetch } from './api';
import { detectShell, registerPush as registerNativePush } from './native-bridge';

export type PushPermission = NotificationPermission | 'unsupported';

export interface PushStatus {
  /**
   * Whether the browser supports the necessary APIs (Service Worker,
   * PushManager, Notification, …). On older Safari, Chrome on iOS PWA
   * mode etc. this can be false.
   */
  supported: boolean;
  /** Current Notification.permission (or 'unsupported'). */
  permission: PushPermission;
  /** True when a live PushSubscription exists for the SW registration. */
  subscribed: boolean;
  /** Endpoint of the current subscription, if any (for diagnostics). */
  endpoint?: string;
  /** True if the VAPID public key is configured at all. */
  vapidConfigured: boolean;
}

export class PushSubscribeError extends Error {
  readonly code: string;
  constructor(code: string, message: string, cause?: unknown) {
    super(message);
    this.name = 'PushSubscribeError';
    this.code = code;
    if (cause !== undefined) {
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

interface PushSubscriptionDto {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent: string;
}

/**
 * Decode a URL-safe base64 VAPID key into the Uint8Array shape
 * `pushManager.subscribe({ applicationServerKey })` requires.
 *
 * The returned view sits on a brand-new ArrayBuffer (NOT a
 * SharedArrayBuffer) so the type satisfies `BufferSource` after
 * TS 5.7's tightening of `Uint8Array` generics.
 */
export function urlBase64ToUint8Array(raw: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (raw.length % 4)) % 4);
  const normalised = (raw + padding).replace(/-/g, '+').replace(/_/g, '/');
  // atob is safe — the spec is ASCII-only.
  const decoded = atob(normalised);
  const buffer = new ArrayBuffer(decoded.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < decoded.length; i += 1) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes as Uint8Array<ArrayBuffer>;
}

function bufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function isSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof window.Notification === 'function'
  );
}

/** Read current state without prompting the user. */
export async function getSubscriptionStatus(): Promise<PushStatus> {
  // Native shells deliver notifications through their own OS channels rather
  // than the Web Push API. Report as supported + permission=granted (the
  // native shell handles its own permission gates) and let the UI render an
  // informational state instead of asking the user to subscribe a SW.
  const shell = detectShell();
  if (shell === 'tauri' || shell === 'capacitor') {
    return {
      supported: true,
      permission: 'granted',
      subscribed: true,
      vapidConfigured: env.VITE_VAPID_PUBLIC_KEY.length > 0,
    };
  }

  const supported = isSupported();
  const vapidConfigured = env.VITE_VAPID_PUBLIC_KEY.length > 0;
  if (!supported) {
    return {
      supported: false,
      permission: 'unsupported',
      subscribed: false,
      vapidConfigured,
    };
  }
  const permission: PushPermission = Notification.permission;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = (await reg?.pushManager.getSubscription()) ?? null;
    return {
      supported: true,
      permission,
      subscribed: !!sub,
      ...(sub?.endpoint ? { endpoint: sub.endpoint } : {}),
      vapidConfigured,
    };
  } catch {
    return {
      supported: true,
      permission,
      subscribed: false,
      vapidConfigured,
    };
  }
}

function toDto(sub: PushSubscription): PushSubscriptionDto {
  const json = sub.toJSON();
  const keys = (json.keys ?? {}) as { p256dh?: string; auth?: string };
  return {
    endpoint: sub.endpoint,
    keys: {
      p256dh: keys.p256dh ?? bufferToBase64Url(sub.getKey('p256dh')),
      auth: keys.auth ?? bufferToBase64Url(sub.getKey('auth')),
    },
    userAgent:
      typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
        ? navigator.userAgent
        : 'unknown',
  };
}

/**
 * Prompts (if needed), creates a PushSubscription against the SW, and
 * registers it with the backend. Idempotent: an existing subscription
 * is returned unchanged after the backend is re-notified, so reload of
 * the settings page re-syncs the server.
 *
 * Native shells (Tauri, Capacitor) short-circuit this flow: their bridge
 * owns the platform permission prompt + transport. We forward the device
 * token (when applicable — Capacitor) to the same backend endpoint so the
 * dispatcher can fan out through APNS/FCM in addition to Web Push.
 */
export async function subscribeToPush(): Promise<PushSubscriptionDto> {
  const shell = detectShell();
  if (shell !== 'web') {
    const native = await registerNativePush();
    if (!native) {
      // Tauri: notifications are delivered locally by the native shell — no
      // server-side fan-out needed. Return a sentinel DTO so call sites can
      // log the result without crashing.
      return {
        endpoint: shell === 'tauri' ? 'native:tauri' : 'native:capacitor',
        keys: { p256dh: '', auth: '' },
        userAgent:
          typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
            ? navigator.userAgent
            : 'native-shell',
      };
    }
    const dto: PushSubscriptionDto = {
      endpoint: `${native.provider}:${native.token}`,
      keys: { p256dh: '', auth: '' },
      userAgent:
        typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
          ? navigator.userAgent
          : `native-${native.provider}`,
    };
    try {
      await apiFetch('/push/subscriptions', {
        method: 'POST',
        body: JSON.stringify(dto),
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      throw new PushSubscribeError(
        'server_register_failed',
        'Subscription created locally but the server rejected it.',
        err,
      );
    }
    return dto;
  }

  if (!isSupported()) {
    throw new PushSubscribeError(
      'unsupported',
      'This browser does not support Web Push notifications.',
    );
  }
  if (!env.VITE_VAPID_PUBLIC_KEY) {
    throw new PushSubscribeError(
      'vapid_missing',
      'Push notifications are not configured for this deployment yet.',
    );
  }

  let permission: NotificationPermission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (err) {
      throw new PushSubscribeError(
        'permission_error',
        'Could not request notification permission.',
        err,
      );
    }
  }
  if (permission !== 'granted') {
    throw new PushSubscribeError('permission_denied', 'Notification permission was not granted.');
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY),
      });
    } catch (err) {
      throw new PushSubscribeError(
        'subscribe_failed',
        'Could not subscribe to push notifications. Check browser settings.',
        err,
      );
    }
  }

  const dto = toDto(sub);
  try {
    await apiFetch('/push/subscriptions', {
      method: 'POST',
      body: JSON.stringify(dto),
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (err) {
    throw new PushSubscribeError(
      'server_register_failed',
      'Subscription created locally but the server rejected it.',
      err,
    );
  }
  return dto;
}

/**
 * Unsubscribes locally and tells the backend to forget the endpoint.
 * Backend failure does NOT block the local unsubscribe — the user
 * clicked "off" and that takes priority.
 *
 * In a native shell we cannot revoke the underlying OS token (the user
 * disables notifications through the OS settings), but we still tell the
 * backend to stop targeting this device so the dispatcher prunes it from
 * the fan-out set.
 */
export async function unsubscribeFromPush(): Promise<void> {
  const shell = detectShell();
  if (shell !== 'web') {
    try {
      await apiFetch('/push/subscriptions', {
        method: 'DELETE',
        body: JSON.stringify({
          endpoint: shell === 'tauri' ? 'native:tauri' : 'native:capacitor',
        }),
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* best-effort */
    }
    return;
  }

  if (!isSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;

  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    // Even when revoke fails, the server-side delete is still useful so
    // we keep going.
  }
  try {
    await apiFetch('/push/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    /* swallow — best-effort */
  }
}

/**
 * Asks the backend to emit a self-test push to all registered
 * subscriptions of the calling user. Useful for the "Send test push"
 * button.
 */
export async function sendTestPush(): Promise<void> {
  await apiFetch('/push/test', { method: 'POST' });
}
